import {useEffect, useRef} from 'react'

interface LoadAllConversationCommentsOptions {
  fetchNextPage: () => Promise<unknown>
  hasNextPage: boolean
  isError: boolean
  isFetchingNextPage: boolean
  isPending: boolean
}

// double rAF to ensure paint
function waitForNextPaint() {
  return new Promise<void>(resolve => requestAnimationFrame(() => requestAnimationFrame(() => resolve())))
}

/** Loads every page of conversation items in sequence. */
export function useLoadAllConversationComments({
  fetchNextPage,
  isFetchingNextPage,
  hasNextPage,
  isPending,
  isError,
}: LoadAllConversationCommentsOptions) {
  // TODO: avoid eagerly loading every page (https://github.com/github/pull-requests/issues/25963)
  const isFetchingNextPageRef = useRef(isFetchingNextPage)
  useEffect(() => {
    if (isPending || isError || isFetchingNextPage || !hasNextPage || isFetchingNextPageRef.current) return

    let cancelled = false
    void (async () => {
      // Give the newly loaded page of comments a chance to paint before starting the next request.
      await waitForNextPaint()
      if (cancelled) return

      isFetchingNextPageRef.current = true
      try {
        await fetchNextPage()
      } finally {
        isFetchingNextPageRef.current = false
      }
    })()

    return () => {
      cancelled = true
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage, isPending, isError])
}
